import React from "react";
import styled from "styled-components";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { DropdownMenuProps } from "@radix-ui/react-dropdown-menu";
import { FontSize } from "@themes/Fonts";
import { ScrollbarStyles } from "@themes/ScrollbarStyles";
import Styles from "@themes/Styles";

/* ------------------------------------------------------------- */
/* ------------ DropdownMenuComponents.Root -------------- */
/* ------------------------------------------------------------- */
interface DropdownRootProps {
    children: React.ReactNode | React.ReactNode[];
}
export function DropdownRoot(props: DropdownRootProps & DropdownMenuProps) {
    return <DropdownMenu.Root modal={false} {...props} />;
}

/* ------------------------------------------------------------- */
/* --------------- DropdownMenuComponents.Content -------------- */
/* ------------------------------------------------------------- */

const StyledDropdownContent = styled(DropdownMenu.Content)`
    padding: unset;
    margin: 4px;
    border-radius: ${Styles.borderRadiusSharp};
    background-color: ${(props) => props.theme.backgroundPrimary};
    min-width: 256px;
    width: fit-content;
    box-shadow: ${(props) => props.theme.boxShadow} ${(props) => props.theme.boxShadowColor};

    max-height: var(--radix-dropdown-menu-content-available-height);
    overflow-y: auto;

    ${ScrollbarStyles};
`;

const StyledDropdownTrigger = styled(DropdownMenu.Trigger)`
    all: unset;
    &[data-disabled] {
        background: ${(props) => props.theme.backgroundSpecial};
        cursor: not-allowed;
        color: ${(props) => props.theme.textColorSecondary};
    }
`;

type DropdownContentProps = {
    children?: React.ReactNode;
    className?: string;
};

export const DropdownContent = ({ children, className }: DropdownContentProps) => {
    const portalDiv = document.getElementById("dropdown-portal");

    return (
        <DropdownMenu.Portal container={portalDiv}>
            <StyledDropdownContent className={className}>{children}</StyledDropdownContent>
        </DropdownMenu.Portal>
    );
};

/* ------------------------------------------------------------- */
/* ---------------- DropdownMenuComponents.Item ---------------- */
/* ------------------------------------------------------------- */

const StyledDropdownItem = styled(DropdownMenu.Item)`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;

    color: ${(props) => props.theme.textColorPrimary};
    font-size: ${FontSize.normal};
    cursor: pointer;

    &[data-highlighted] {
        background-color: ${(props) => props.theme.hoverPrimary};
        outline: none;
    }

    &[data-disabled] {
        color: ${(props) => props.theme.textColorSecondary};
        background-color: ${(props) => props.theme.backgroundSpecial};
        cursor: not-allowed;
    }
`;

interface DropdownItemProps {
    readonly onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
    readonly disabled?: boolean;
    readonly children: React.ReactNode[] | React.ReactNode;
}
function DropdownItem(props: DropdownItemProps) {
    return (
        <StyledDropdownItem
            onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                props.onClick(e);
            }}
            disabled={props.disabled}
        >
            {props.children}
        </StyledDropdownItem>
    );
}

/* ------------------------------------------------------------- */
/* ------------ DropdownMenuComponents.HeaderItem -------------- */
/* ------------------------------------------------------------- */

const StyledDropdownHeaderItem = styled(DropdownMenu.Label)`
    padding: 8px;
    color: ${(props) => props.theme.textColorPrimary};
    font-size: ${FontSize.normal};
    font-weight: bold;
`;

interface DropdownHeaderItemProps {
    readonly label: string;
}
export function DropdownHeaderItem(props: DropdownHeaderItemProps) {
    return <StyledDropdownHeaderItem>{props.label}</StyledDropdownHeaderItem>;
}

/* ------------------------------------------------------------- */
/* ------------ DropdownMenuComponents.Separator -------------- */
/* ------------------------------------------------------------- */

const StyledDropdownSeparator = styled(DropdownMenu.Separator)<{ $bold: boolean }>`
    height: 1px;
    background-color: ${(props) => (props.$bold ? props.theme.borderMono : props.theme.borderPrimary)};
`;

interface DropdownSeparatorProps {
    readonly bold?: boolean;
}
export function DropdownSeparator(props: DropdownSeparatorProps) {
    return <StyledDropdownSeparator $bold={!!props.bold} />;
}

export const DropdownLabel = styled(DropdownMenu.Label)`
    padding: 8px;

    color: ${(props) => props.theme.textColorPrimary};
    font-size: ${FontSize.normal};
`;

export const DropdownMenuComponents = {
    Root: DropdownRoot,
    Trigger: StyledDropdownTrigger,
    Content: DropdownContent,
    Item: DropdownItem,
    HeaderItem: DropdownHeaderItem,
    Separator: DropdownSeparator,
    Label: DropdownLabel,
};
